/**
 * The avatar menu, 1:1 with the K3 shell: who is signed in, the Ishda / Tanaffus / Ishda emas
 * switch, the person's own sales this week, the profile's sections, the dark rail, and a red
 * Chiqish. The week line is the caller's own number and nothing else (D9.109). "Agentlik
 * sozlamalari" is the owner's alone. Changing agency is in the sidebar's agency block.
 */
import * as RadixPopover from "@radix-ui/react-popover";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Bell, Building2, CircleHelp, Globe, Keyboard, LogOut, Monitor, PanelLeft, Sun, UserRound } from "lucide-react";
import { useEffect, useState, type JSX, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";

import { profileApi, type ManualStatus } from "@/api/profile";
import { queryKeys } from "@/api/queryKeys";
import { Modal } from "@/components/ui";
import { activeLocale, useTranslation, type TranslationKey } from "@/i18n";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/store/auth";
import { Avatar } from "@/ui/avatar";
import { Switch } from "@/ui/switch";

import { useShellPrefs } from "./shellPrefs";
import { statusDotClass } from "./statusDot";

const STATUSES: readonly ManualStatus[] = ["working", "break", "off"];

const ROW = "flex h-9 w-full items-center gap-2.5 rounded-small px-2.5 text-left text-sm text-ink transition-colors duration-fast ease-token hover:bg-surface-muted [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-ink-subtle";

function useShortcutDialog(): [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if ((event.metaKey || event.ctrlKey) && event.key === "/") {
        event.preventDefault();
        setOpen(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return (): void => {
      window.removeEventListener("keydown", onKey);
    };
  }, []);
  return [open, setOpen];
}

function MenuBlock({ children }: { children: ReactNode }): JSX.Element {
  return <div className="border-t border-line p-1.5">{children}</div>;
}

export function UserMenu(): JSX.Element | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const dark = useShellPrefs((state) => state.dark);
  const setDark = useShellPrefs((state) => state.setDark);
  const [open, setOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useShortcutDialog();
  const [helpOpen, setHelpOpen] = useState(false);
  const inAgency = user?.tenant != null;

  const availability = useQuery({
    queryKey: queryKeys.profile.availability(),
    queryFn: profileApi.availability,
    enabled: inAgency,
  });
  const week = useQuery({
    queryKey: queryKeys.profile.myWeek(),
    queryFn: profileApi.myWeek,
    enabled: inAgency,
  });
  const preferences = useQuery({
    queryKey: queryKeys.profile.preferences(),
    queryFn: profileApi.preferences,
    enabled: inAgency,
  });
  const setStatus = useMutation({
    mutationFn: (status: ManualStatus) => profileApi.updateAvailability({ manual_status: status }),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.profile.availability(), data);
      void queryClient.invalidateQueries({ queryKey: queryKeys.team.status() });
    },
  });

  if (user === null) return null;

  const close = (): void => {
    setOpen(false);
  };
  const link = (to: string, icon: ReactNode, label: string, hint?: string): JSX.Element => (
    <Link to={to} onClick={close} className={ROW}>
      {icon}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {hint === undefined ? null : <span className="text-xs text-ink-subtle">{hint}</span>}
    </Link>
  );

  const manual = availability.data?.manual_status;
  const effective = availability.data?.effective_status;
  const theme = preferences.data?.theme;
  const subtitle = [user.role === null ? null : t(`role.${user.role}` as TranslationKey), user.branch?.name ?? null]
    .filter((part): part is string => part !== null)
    .join(" · ");

  return (
    <>
      <RadixPopover.Root open={open} onOpenChange={setOpen}>
        <RadixPopover.Trigger asChild>
          <button type="button" className="relative shrink-0 rounded-pill p-0.5">
            <Avatar name={user.full_name} src={user.avatar_url} size="md" />
            <span className="sr-only">{t("shell.menu.userLabel")}</span>
            <span
              aria-label={effective === undefined ? undefined : t(`status.${effective}` as TranslationKey)}
              role={effective === undefined ? undefined : "img"}
              className={cn("absolute right-px bottom-px size-2.5 rounded-pill outline-2 outline-canvas", statusDotClass(effective))}
            />
          </button>
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <RadixPopover.Content
            align="end"
            sideOffset={6}
            className="z-50 max-h-[calc(100dvh-72px)] w-75 max-w-[calc(100vw-24px)] overflow-y-auto rounded-card border border-line bg-surface shadow-overlay"
          >
            <div className="flex items-center gap-2.5 p-3.5">
              <Avatar name={user.full_name} src={user.avatar_url} size="xl" />
              <div className="flex min-w-0 flex-col">
                <b className="truncate text-base font-semibold">{user.full_name}</b>
                {subtitle === "" ? null : <span className="truncate text-xs text-ink-muted">{subtitle}</span>}
                <span className="truncate text-xs text-ink-subtle">{user.email}</span>
              </div>
            </div>
            {inAgency ? (
              <>
                <div className="flex flex-col gap-1.5 px-3.5 pb-3">
                  <span className="text-xs font-medium text-ink-subtle">{t("shell.menu.myStatus")}</span>
                  <div role="group" aria-label={t("shell.menu.myStatus")} className="grid grid-cols-3 gap-0.5 rounded-control bg-surface-muted p-0.5">
                    {STATUSES.map((status) => (
                      <button
                        key={status}
                        type="button"
                        aria-pressed={manual === status}
                        onClick={() => {
                          setStatus.mutate(status);
                        }}
                        className={cn(
                          "inline-flex h-7.5 items-center justify-center gap-1.5 rounded-small text-xs whitespace-nowrap",
                          manual === status ? "bg-surface font-medium text-ink" : "text-ink-muted",
                        )}
                      >
                        <span className={cn("size-1.75 shrink-0 rounded-pill", statusDotClass(status))} />
                        {t(`status.${status}` as TranslationKey)}
                      </button>
                    ))}
                  </div>
                  <span className="text-xs text-ink-subtle">{t("shell.menu.statusNote")}</span>
                </div>
                <Link
                  to="/hisobotlar/mening"
                  onClick={close}
                  className="mx-2 mb-2 flex items-center gap-2 rounded-control border border-line bg-canvas px-2.5 py-2 text-sm text-ink hover:bg-surface-muted"
                >
                  <span className="flex-1">{t("shell.menu.weekLine", { count: week.data?.won ?? 0 })}</span>
                  <span className="inline-flex items-center gap-1 font-medium text-accent-ink">
                    {t("shell.menu.myReport")}
                    <ArrowRight className="size-3.5" strokeWidth={1.5} aria-hidden="true" />
                  </span>
                </Link>
              </>
            ) : null}
            <MenuBlock>
              {inAgency ? (
                <>
                  {link("/profile?section=shaxsiy", <UserRound strokeWidth={1.5} />, t("shell.menu.profileSettings"))}
                  {link("/profile?section=bildirishnomalar", <Bell strokeWidth={1.5} />, t("shell.menu.notifications"))}
                  {link(
                    "/profile?section=korinish",
                    <Sun strokeWidth={1.5} />,
                    t("shell.menu.theme"),
                    theme === undefined ? undefined : t(`theme.${theme}` as TranslationKey),
                  )}
                  {link("/profile?section=korinish", <Globe strokeWidth={1.5} />, t("shell.menu.language"), t(`language.${activeLocale()}` as TranslationKey))}
                </>
              ) : null}
              <label className={cn(ROW, "hidden cursor-pointer lg:flex")}>
                <PanelLeft strokeWidth={1.5} />
                <span className="min-w-0 flex-1 truncate">{t("shell.menu.darkSidebar")}</span>
                <Switch checked={dark} onCheckedChange={setDark} label={t("shell.menu.darkSidebar")} />
              </label>
              <button
                type="button"
                className={ROW}
                onClick={() => {
                  close();
                  setShortcutsOpen(true);
                }}
              >
                <Keyboard strokeWidth={1.5} />
                <span className="flex-1">{t("shell.menu.shortcuts")}</span>
                <span className="text-xs text-ink-subtle">⌘ /</span>
              </button>
              <button
                type="button"
                className={ROW}
                onClick={() => {
                  close();
                  setHelpOpen(true);
                }}
              >
                <CircleHelp strokeWidth={1.5} />
                <span className="flex-1">{t("shell.menu.help")}</span>
              </button>
            </MenuBlock>
            {user.role === "owner" || user.is_platform_admin ? (
              <MenuBlock>
                {user.role === "owner" ? link("/sozlamalar", <Building2 strokeWidth={1.5} />, t("shell.menu.agencySettings")) : null}
                {user.is_platform_admin ? link("/platforma/tenantlar", <Monitor strokeWidth={1.5} />, t("shell.menu.platform")) : null}
              </MenuBlock>
            ) : null}
            <MenuBlock>
              <button
                type="button"
                className="flex h-9 w-full items-center gap-2.5 rounded-small px-2.5 text-sm text-danger-ink hover:bg-danger-soft"
                onClick={() => {
                  logout();
                  void navigate("/kirish", { replace: true });
                }}
              >
                <LogOut className="size-4" strokeWidth={1.5} aria-hidden="true" />
                {t("auth.logout")}
              </button>
            </MenuBlock>
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
      {shortcutsOpen ? (
        <Modal
          title={t("shell.shortcuts.title")}
          onClose={() => {
            setShortcutsOpen(false);
          }}
        >
          <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-base">
            <dt className="font-mono text-ink-muted">⌘ K</dt>
            <dd className="text-ink">{t("shell.shortcuts.palette")}</dd>
            <dt className="font-mono text-ink-muted">⌘ /</dt>
            <dd className="text-ink">{t("shell.shortcuts.help")}</dd>
          </dl>
        </Modal>
      ) : null}
      {helpOpen ? (
        <Modal
          title={t("shell.menu.help")}
          onClose={() => {
            setHelpOpen(false);
          }}
        >
          <p className="text-base text-ink-muted">{t("shell.help.body")}</p>
        </Modal>
      ) : null}
    </>
  );
}
